import type { LayoutScopeKey } from 'modules/manager/domain/match-layout';
import React from 'react';

/**
 * Everything the «Раскладка …» and «Фильтрация …» sections' Russian prose needs
 * in order to talk about one kind of slot.
 *
 * Russian inflects by gender and case, so a scope cannot be described by a
 * single noun — «строка» is feminine and «блок» is masculine, which is why the
 * copy this replaced had to branch even mid-word
 * («при одн{isStandings ? 'ой строке' : 'ом блоке'}»). Every form the copy uses
 * is its own field here, so the section bodies contain no ternaries at all and a
 * new scope is one new entry.
 */
export interface LayoutScopeDescriptor {
  /** The `data.*` field addressing a slot. Must equal this entry's record key. */
  scopeKey: LayoutScopeKey;

  // — «Раскладка …» section
  /** InspectorSection title, e.g. «Раскладка матчей». */
  sectionTitle: string;
  /** Nominative plural as subject/object: «пустые {unitPlural} скрываются». */
  unitPlural: string;
  /** Genitive plural: «оставшееся от скрытых {unitPluralGen}». */
  unitPluralGen: string;
  /** Capitalized nominative plural, sentence-initial: «{unitPluralCap} определяются…». */
  unitPluralCap: string;
  /** Singular after a numeral 2-4: «минимум 2 оставшихся {unitCountedTwo}». */
  unitCountedTwo: string;
  /** Prepositional singular including the numeral: «при {unitSingularPrep}». */
  unitSingularPrep: string;
  /** «расположены {stackedAdverbial}»: «одна под другой» / «один под другим». */
  stackedAdverbial: string;
  /** Where the slot number is read from, after «определяются автоматически по». */
  autoSource: string;
  /**
   * Genitive plural counted by the preview field: «будто передано только N
   * {previewUnitGen}». Its own field rather than `unitPluralGen` because the
   * preview counts the *data* passed to the render (матчей, строк), not the
   * template's drawn bundles («блоков матчей»).
   */
  previewUnitGen: string;
  /**
   * Section-level help. A whole node rather than word-parts: these paragraphs
   * are structurally different per scope (one argues about 8 group rows, one
   * about 6 match blocks), so word-level parameterization would be false economy.
   */
  sectionHelp: React.ReactNode;

  // — «Фильтрация …» section
  filterSectionTitle: string;
  filterFieldLabel: string;
  filterHelp: React.ReactNode;
}

/**
 * `Record<LayoutScopeKey, …>` on purpose — the same trick TEMPLATE_TYPE_REGISTRY
 * uses. Widening `LayoutScopeKey` is a compile error until a descriptor exists,
 * so a new scope can never reach the UI half-described.
 */
export const LAYOUT_SCOPE_DESCRIPTORS: Readonly<Record<LayoutScopeKey, LayoutScopeDescriptor>> = {
  matchNumber: {
    scopeKey: 'matchNumber',
    sectionTitle: 'Раскладка матчей',
    unitPlural: 'блоки матчей',
    unitPluralGen: 'блоков матчей',
    unitPluralCap: 'Блоки',
    unitCountedTwo: 'блока',
    unitSingularPrep: 'одном блоке',
    stackedAdverbial: 'один под другим',
    autoSource: 'значению matchNumber у плейсхолдеров',
    previewUnitGen: 'матчей',
    sectionHelp: (
      <span>
        Шаблон рисуется с фиксированным числом блоков матчей (например, 6). Если реальных матчей меньше (например, 4), лишние блоки
        шаблона просто скрываются и не попадают в итоговое изображение. Эта настройка решает, что происходит с оставшимися видимыми
        блоками: остаться ровно там, где их разместили при вёрстке шаблона (тогда в области останется пустое место), или сдвинуться
        так, чтобы аккуратно заполнить область, где раньше были нарисованы все 6 блоков. На сами данные (какой матч в каком блоке) это
        не влияет — меняется только то, где блок нарисован на картинке.
      </span>
    ),
    filterSectionTitle: 'Фильтрация по матчу',
    filterFieldLabel: 'Матч №',
    filterHelp: (
      <span>
        Скрывает каждый выбранный объект, если матч N не рендерится. <code>1</code> = первый матч из /render, <code>2</code> = второй и
        т.д. Используется только для мульти-матч шаблонов (например, «Несколько матчей»).
      </span>
    ),
  },
  teamNumber: {
    scopeKey: 'teamNumber',
    sectionTitle: 'Раскладка строк',
    unitPlural: 'строки',
    unitPluralGen: 'строк',
    unitPluralCap: 'Строки',
    unitCountedTwo: 'строки',
    unitSingularPrep: 'одной строке',
    stackedAdverbial: 'одна под другой',
    autoSource: 'значению «Позиция команды» у плейсхолдеров',
    previewUnitGen: 'строк',
    sectionHelp: (
      <span>
        Шаблон турнирной таблицы рисуется с фиксированным числом строк (например, 8 — по числу мест в группе). Если в реальной группе
        команд меньше (например, 5), лишние строки шаблона просто скрываются и не попадают в итоговое изображение. Эта настройка
        решает, что происходит с оставшимися видимыми строками: остаться ровно там, где их разместили при вёрстке шаблона (тогда в
        области останется пустое место), или сдвинуться так, чтобы аккуратно заполнить область, где раньше были нарисованы все 8 строк.
        На сами данные (какая команда на каком месте) это не влияет — меняется только то, где строка нарисована на картинке.
      </span>
    ),
    filterSectionTitle: 'Фильтрация по позиции',
    filterFieldLabel: 'Позиция команды',
    filterHelp: (
      <span>
        Привязывает выбранный объект к строке таблицы. <code>1</code> = первое место, <code>2</code> = второе и т.д. Если в группе
        меньше команд, чем строк в шаблоне, объекты лишних строк убираются при рендере. Используется только для шаблонов «Турнирная
        таблица».
      </span>
    ),
  },
  playerNumber: {
    scopeKey: 'playerNumber',
    sectionTitle: 'Раскладка игроков',
    unitPlural: 'блоки игроков',
    unitPluralGen: 'блоков игроков',
    // «блок» is masculine — the same forms as матч, unlike the feminine «строка».
    unitPluralCap: 'Блоки',
    unitCountedTwo: 'блока',
    unitSingularPrep: 'одном блоке',
    stackedAdverbial: 'один под другим',
    autoSource: 'значению «Игрок №» у плейсхолдеров',
    previewUnitGen: 'игроков',
    sectionHelp: (
      <span>
        Шаблон статистики игроков рисуется с фиксированным числом мест (например, 5 — топ-5). Если в рейтинге игроков меньше (например,
        3) или в настройке «Ранжирование» задан меньший «Топ N», лишние блоки шаблона просто скрываются и не попадают в итоговое
        изображение. Эта настройка решает, что происходит с оставшимися видимыми блоками: остаться ровно там, где их разместили при
        вёрстке шаблона (тогда в области останется пустое место), или сдвинуться так, чтобы аккуратно заполнить область, где раньше
        были нарисованы все 5 блоков. На сами данные (какой игрок на каком месте) это не влияет — меняется только то, где блок
        нарисован на картинке.
      </span>
    ),
    filterSectionTitle: 'Фильтрация по месту в рейтинге',
    filterFieldLabel: 'Игрок №',
    filterHelp: (
      <span>
        Привязывает выбранный объект к месту в рейтинге. <code>1</code> = первое место, <code>2</code> = второе и т.д. Если игроков в
        рейтинге меньше, чем мест в шаблоне, объекты лишних мест убираются при рендере. Используется только для шаблонов «Статистика
        игроков».
      </span>
    ),
  },
};
